import AlertMinimalIcon from '@assets/icons/alert-minimal.svg';
import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { addToOrderPrebookItemDisabled } from '@/contexts/item-context-utils';
import { BadgeLabelList, MAX_VISIBLE_BADGES_MOBILE } from './badge-label-list';
import { BORDER, TYPOGRAPHY } from '@/styles';
import { createStyles } from '@/contexts/theme-context';
import { EqualHeightTouchableOpacity } from '@/components/equal-height-touchable-opacity';
import { formatDate } from '@/utils/date';
import { ItemListOption } from '@/contexts/item-context-types';
import { ItemSubtitleV2 } from '@/components/item-subtitle-v2';
import { Label } from '@/components/label';
import { OrderableItemListItemImage } from './orderable-item-list-item-image';
import { QuantitySelector } from '@/components/quantity-selector';
import { Text, View } from 'react-native';
import {
  useIsPhotoHidden,
  useItemDisplayOptions,
  useNewOrderContext,
} from '@/contexts/new-order-context';
import { useItemLabels } from '@/hooks/use-item-labels';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

export const SMART_ORDER_LIST_ITEM_WIDTH = 300;

interface Props {
  item: ItemListOption;
  onSelect: () => void;
  quantity: number;
  updateQuantity: (quantity: number) => void;
}

export const SmartOrderListItem: React.FC<Props> = ({
  item,
  onSelect,
  quantity,
  updateQuantity,
}) => {
  const [styles, theme] = useStyles();
  const { essentialVariantPackUUIDs, lastPurchaseInfoMap } =
    useVariantPackInfoContext();
  const { supplierTimezone } = useNewOrderContext();
  const { hidePlaceholderImages } = useItemDisplayOptions();

  const metadata = item.metadata!;
  const variantPackUUID = metadata.variantPackUUID;
  const lastPurchaseInfo = lastPurchaseInfoMap[variantPackUUID];
  const isPhotoHidden = useIsPhotoHidden(metadata);
  const isEssential = essentialVariantPackUUIDs.has(variantPackUUID);
  const altText = [metadata.displayName, metadata.externalID]
    .filter(Boolean)
    .join('-');

  const isAddToOrderPrebookItemDisabled = useMemo(
    () => addToOrderPrebookItemDisabled(metadata, supplierTimezone),
    [metadata, supplierTimezone],
  );

  const itemLabels = useItemLabels(metadata);

  return (
    <EqualHeightTouchableOpacity onPress={onSelect} style={styles.base}>
      {lastPurchaseInfo ? (
        <Label
          backgroundColor={theme.colors.accentMedium}
          labelStyle={styles.lastPurchaseLabel}
          text={i18n.t('last_purchased_date', {
            date: formatDate(lastPurchaseInfo.orderDate),
          })}
          textStyle={[TYPOGRAPHY.xsmall, styles.lastPurchaseText]}
        />
      ) : null}
      <View style={styles.row}>
        {isPhotoHidden ? null : (
          <View style={styles.image}>
            <OrderableItemListItemImage
              alt={altText}
              hidePlaceholderImages={hidePlaceholderImages}
              imageURLs={metadata.photoURLs}
            />
          </View>
        )}
        <View style={styles.description}>
          <Text numberOfLines={1} style={TYPOGRAPHY.pSemiBold}>
            {metadata.displayName}
          </Text>
          <Text numberOfLines={2} style={TYPOGRAPHY.description}>
            {metadata.description}
          </Text>
          <BadgeLabelList
            badges={metadata.badges}
            iconOnly
            maxVisible={MAX_VISIBLE_BADGES_MOBILE}
          />
        </View>
      </View>
      <View style={[styles.row, styles.itemDetailsContainer]}>
        <ItemSubtitleV2
          currencyCode={metadata.currencyCode}
          displayString={metadata.packDisplayString}
          labels={itemLabels}
          sku={metadata.externalID}
          unitCount={metadata.unitCount}
          unitPriceInMicros={metadata.unitPriceInMicros}
        />
        {!isAddToOrderPrebookItemDisabled ? (
          <View style={styles.quantitySelectorContainer}>
            <QuantitySelector
              additionalIcon={isEssential ? AlertMinimalIcon : null}
              canDecrementToZero
              isFocused={false}
              onChange={(_, newQuantity) => updateQuantity(newQuantity)}
              quantity={quantity}
            />
          </View>
        ) : null}
      </View>
    </EqualHeightTouchableOpacity>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    borderColor: theme.colors.stroke,
    borderRadius: 4,
    borderWidth: 1,
    gap: 8,
    padding: 8,
    width: SMART_ORDER_LIST_ITEM_WIDTH,
  },
  description: {
    flex: 1,
    overflow: 'hidden',
  },
  image: {
    marginRight: 8,
  },
  itemDetailsContainer: {
    ...BORDER.primary,
    padding: 8,
  },
  lastPurchaseLabel: {
    borderRadius: 30,
    paddingVertical: 4,
  },
  lastPurchaseText: {
    textTransform: 'none',
  },
  quantitySelectorContainer: {
    marginTop: 4,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
}));
